import { arrayIncludes } from '@ag1/ui';
import { getProductsInventoryByVariantId } from '@portal/api/shopify/products-availability';
import { noopPerformanceLogger, PerformanceLogger } from '@portal/api/utils/performanceLogger';
import { StoreEnum } from '@portal/schema/storeCode';
import type { PageSection } from '@portal/types/studio/page';
import { loadShopifyCollectionProducts, loadShopifyProducts } from 'api/shopifyV2/products';
import type { ShopifyResultProductsMap } from 'api/shopifyV2/types';
import { CollectionProduct, Product } from 'api/shopifyV2/types';
import {
  getShopifyProductsMap,
  getShopifyProductVariantIDs,
  getShopifyProductVariantIDsForCart,
  getShopifyProductVariantIDsForSimplifiedBaB,
  hasUsableBaBCollection
} from 'api/shopifyV2/utils';
import type { AssetLibrarySection, DynamicSlice } from '../DynamicLanders/types';
import { type ComponentMapType } from './componentsMap';

/**
 * Returns whether or not the given section is one of the given types. Will also
 * narrow the section properly within typescript.
 */
export const isSectionType = <T extends readonly ComponentMapType[]>(
  section: PageSection | AssetLibrarySection | undefined,
  sectionTypes: T
): section is PageSection & { _type: (typeof sectionTypes)[number] } => {
  return !!section && '_type' in section && arrayIncludes(sectionTypes, section._type);
};

export type FetchUsedSanityProductsResult = Awaited<ReturnType<typeof fetchUsedSanityProducts>>;
export type UsedProductsMap = FetchUsedSanityProductsResult['usedProductsMap'];

type Section = NonNullable<DynamicSlice['sections']>[number] | PageSection;
type Args = {
  sections?: Section[];
  store: StoreEnum;
  performanceLogger?: PerformanceLogger;
};

function getAllVariantIdsFromBuildBundleOrAddToCartSections({ sections, store }: Args) {
  if (!sections) {
    return [];
  }

  const productVariantsIds: string[] = [];

  const buildBundleSection = sections.find((section) => isSectionType(section, ['buildBundleV3']));
  const addToCartSection = sections.find((section) =>
    isSectionType(section, ['addToCartSection', 'addToCartSectionShop', 'simplifiedBaB'])
  );

  if (buildBundleSection) {
    const variants = ['firstVariant', 'secondVariant'] as const;
    const subTypes = [
      'singleSubscription',
      'doubleSubscription',
      'familySubscription',
      'oneTime'
    ] as const;
    const productTypes = ['ProductIds', 'welcomeKitIds', 'gifts', 'giftProducts'] as const;

    variants.forEach((variant) =>
      subTypes.forEach((subType) =>
        productTypes.forEach((productType) => {
          const variantIds = buildBundleSection?.[variant]?.[subType]?.[productType]?.reduce(
            (acc, product) => {
              if (product.targetStore === store) {
                if (
                  'productVariantId' in product &&
                  product.productVariantId &&
                  !productVariantsIds.includes(product.productVariantId)
                ) {
                  acc.push(product.productVariantId);
                } else if (
                  'welcomeKitId' in product &&
                  product.welcomeKitId &&
                  !productVariantsIds.includes(product.welcomeKitId)
                ) {
                  acc.push(product.welcomeKitId);
                } else if (
                  'variantId' in product &&
                  product.variantId &&
                  !productVariantsIds.includes(product.variantId)
                ) {
                  acc.push(product.variantId);
                }
              }

              return acc;
            },
            [] as string[]
          );

          if (variantIds && variantIds.length > 0) {
            productVariantsIds.push(...variantIds);
          }
        })
      )
    );
  } else if (addToCartSection) {
    const subTypes = [
      'singleSubscription',
      'doubleSubscription',
      'familySubscription',
      'oneTime'
    ] as const;
    const productTypes = ['productIds', 'welcomeKitIds', 'giftProducts'] as const;

    const variants: string[] = [];
    addToCartSection.variants.forEach((variant: any) => {
      subTypes.forEach((subType) =>
        productTypes.forEach((productType) => {
          let currentVariants: string[] = [];

          if (productType === 'welcomeKitIds') {
            currentVariants = variant[subType]?.[productType] || [];
          } else if (productType === 'productIds') {
            currentVariants =
              variant?.[subType]?.[productType]?.map((product: any) => product.productVariantId) ||
              [];
          } else if (productType === 'giftProducts') {
            currentVariants =
              variant?.[subType]?.[productType]?.map((gift: any) => gift.variantId) || [];
          }

          if (currentVariants?.length > 0) {
            variants.push(...currentVariants);
          }
        })
      );
    });

    if (variants?.length > 0) {
      productVariantsIds.push(
        ...variants.filter((variant, index, array) => array.indexOf(variant) === index)
      );
    }
  }

  return productVariantsIds;
}

export const fetchUsedSanityProducts = async ({
  sections,
  store,
  performanceLogger = noopPerformanceLogger
}: Args) => {
  const shopifyLogger = performanceLogger.child('shopify');

  const sectionWithBuildBundle = sections?.find((section) =>
    isSectionType(section, [
      'buildBundleV3',
      'addToCartSection',
      'addToCartSectionShop',
      'simplifiedBaB'
    ])
  );

  const hasBuildABundleV3 = sections?.some((section) => isSectionType(section, ['buildBundleV3']));
  const hasAddToCartShop = sections?.some((section) =>
    isSectionType(section, ['addToCartSectionShop'])
  );
  const hasSimplifiedBaB = sections?.some((section) => isSectionType(section, ['simplifiedBaB']));

  let shopifyProducts: CollectionProduct[] | Product[] = [];

  if (hasBuildABundleV3 || hasAddToCartShop || hasSimplifiedBaB) {
    try {
      // load shopify products through collection query if sanity & shopify collection
      // are both set up properly
      if (hasUsableBaBCollection(store)) {
        const [shopifyProductsBab, shopifyProductsBaBv53, shopifyProductsAnz] = await Promise.all([
          shopifyLogger.time(
            'loadShopifyCollectionProducts:bab',
            loadShopifyCollectionProducts(store, 'bab')
          ),
          shopifyLogger.time(
            'loadShopifyCollectionProducts:bab-v53',
            loadShopifyCollectionProducts(store, 'bab-v53')
          ),
          shopifyLogger.time(
            'loadShopifyCollectionProducts:anz',
            loadShopifyCollectionProducts(store, 'anz')
          )
        ]);

        shopifyProducts = [...shopifyProductsBab, ...shopifyProductsBaBv53, ...shopifyProductsAnz];
      } else {
        shopifyProducts = await shopifyLogger.time(
          'loadShopifyProducts',
          loadShopifyProducts(store)
        );
      }
    } catch (err) {
      throw new Error('Error loading products');
    }
  }

  let usedProductsMap: ShopifyResultProductsMap = {};

  if (sectionWithBuildBundle) {
    if (sectionWithBuildBundle._type === 'buildBundleV3') {
      const productVariantIds = getShopifyProductVariantIDs(
        sectionWithBuildBundle.firstVariant,
        sectionWithBuildBundle.secondVariant,
        store
      );

      usedProductsMap = getShopifyProductsMap(shopifyProducts, productVariantIds);
    } else if (sectionWithBuildBundle._type === 'addToCartSectionShop') {
      const productVariantIds = getShopifyProductVariantIDsForCart(
        sectionWithBuildBundle.variants,
        store
      );

      usedProductsMap = getShopifyProductsMap(shopifyProducts, productVariantIds);
    } else if (sectionWithBuildBundle._type === 'simplifiedBaB') {
      const productVariantIds = getShopifyProductVariantIDsForSimplifiedBaB(
        sectionWithBuildBundle.variants,
        store
      );

      usedProductsMap = getShopifyProductsMap(shopifyProducts, productVariantIds);
    }
  }

  const productVariantsIds = getAllVariantIdsFromBuildBundleOrAddToCartSections({
    sections,
    store
  });

  const productsInventoryByVariantId = await shopifyLogger.time(
    'getProductsInventoryByVariantId',
    getProductsInventoryByVariantId(productVariantsIds, store)
  );

  return {
    productsInventory: productsInventoryByVariantId ? productsInventoryByVariantId.products : [],
    usedProductsMap
  };
};

/**
 * Internal class for building page section props while maintaining type safety.
 */
class PageSectionPropsBuilder<T extends PageSection> {
  public value: T;

  constructor(value: T) {
    this.value = value;
  }

  public addForTypes<
    ValidSectionTypes extends readonly ComponentMapType[],
    PropsToAdd extends Record<string, unknown>
  >(
    types: ValidSectionTypes,
    props: PropsToAdd
  ): PageSectionPropsBuilder<T extends { _type: ValidSectionTypes[number] } ? T & PropsToAdd : T> {
    if (isSectionType(this.value, types)) {
      this.value = { ...this.value, ...props } as any;
    }

    return this as any;
  }

  public getProps(): T {
    return this.value;
  }
}

/**
 * This builder helps define which props are required for which section types so
 * that Typescript can properly enforce them for the provided section object.
 */
export const pageSectionPropsBuilder = <T extends PageSection>(
  value: T
): PageSectionPropsBuilder<T> => {
  return new PageSectionPropsBuilder<T>(value);
};
